import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import {
  SessionsList,
  useCoreSessionFilters,
  useSessionCrossIdFilters,
} from "@/shared/components/sessions";

import { useDeviceContext } from "../Device/useDeviceContext";

/**
 * Body of the Sessions tab on the device-detail page. Renders the shared
 * `<SessionsList>` scoped to the current device; the scope is enforced
 * server-side via the `device.device_id` cross-aggregate filter and cannot
 * be widened by the user.
 */
export function DeviceSessions() {
  const { deviceId } = useDeviceContext();
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const coreFilters = useCoreSessionFilters(resolvedOrgId);
  const crossIdFilters = useSessionCrossIdFilters(resolvedOrgId);

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "device.device_id",
        comparator: "EQUALS",
        values: [deviceId],
      },
    ],
    [deviceId],
  );

  // The device id is the server-side scope, so the picker omits it and
  // surfaces only the dataset id as the cross-aggregate narrowing axis.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [...coreFilters, crossIdFilters.datasetIdFilter],
    [coreFilters, crossIdFilters],
  );

  return (
    <SessionsList
      scope={scope}
      filterDefinitions={filterDefinitions}
      noData={{ title: "No sessions for this device" }}
    />
  );
}
